// Adapted from Dealtracker src/appframe/tours/avoidSet.ts (commit 30ca0cb). Dealtracker finds its
// popups through Fluent's `[data-portal-node]`; Niobe's popups are custom portals, so they are
// found by a selector list instead (POPUP_SELECTOR). The hook itself is unchanged.
import { useLayoutEffect, useState } from 'react'
import { helpIdSelector, type HelpId } from './helpIds'
import type { Rect } from './HighlightRing'
import type { Viewport } from './placement'

/** Something the step window must not cover, besides the target itself. */
export interface AvoidItem {
  el: Element
  rect: Rect
  /** `popup`: a dialog / menu / listbox the user opened; `window`: an element the step declares. */
  kind: 'popup' | 'window'
}

export interface AvoidSet {
  items: AvoidItem[]
  viewport: Viewport
}

/**
 * Niobe's open popups: dialog surfaces, menus, listboxes, and anything a component opts in with
 * `data-tour-avoid` (a colour picker, a link popover…).
 */
export const POPUP_SELECTOR = '[role="dialog"], [role="alertdialog"], [aria-modal="true"], [role="menu"], [role="listbox"], [data-tour-avoid]'
/** The tour's own UI, never an obstacle: the step window, the cards, the ring. */
export const TOUR_WINDOW = '[data-tour-window]'
/** A modal's backdrop covers the whole screen: only the dialog surface inside it is an obstacle. */
const MODAL_BACKDROP = '.modal-backdrop, .drawer-backdrop'

function rectOf(el: Element): Rect | null {
  const r = el.getBoundingClientRect()
  return r.width > 0 && r.height > 0 ? { top: r.top, left: r.left, width: r.width, height: r.height } : null
}

/** Every open popup, outermost only (a listbox inside a dialog is covered by the dialog's rect). */
function popups(): AvoidItem[] {
  const out: AvoidItem[] = []
  const found = [...document.querySelectorAll(POPUP_SELECTOR)]
    .filter((el) => !el.closest(TOUR_WINDOW) && !el.matches(MODAL_BACKDROP))
  for (const el of found) {
    if (found.some((other) => other !== el && other.contains(el))) continue
    const rect = rectOf(el)
    if (rect) out.push({ el, rect, kind: 'popup' })
  }
  return out
}

function windows(ids: readonly HelpId[]): AvoidItem[] {
  return ids.flatMap((id) => [...document.querySelectorAll(helpIdSelector(id))].flatMap((el) => {
    const rect = rectOf(el)
    return rect ? [{ el, rect, kind: 'window' as const }] : []
  }))
}

export function collectAvoidSet(avoid: readonly HelpId[]): AvoidSet {
  return {
    items: [...popups(), ...windows(avoid)],
    viewport: { width: window.innerWidth, height: window.innerHeight },
  }
}

const sameRect = (a: Rect, b: Rect) => a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height
function same(a: AvoidSet, b: AvoidSet): boolean {
  return a.viewport.width === b.viewport.width && a.viewport.height === b.viewport.height
    && a.items.length === b.items.length
    && a.items.every((x, i) => {
      const y = b.items[i]
      return !!y && x.el === y.el && x.kind === y.kind && sameRect(x.rect, y.rect)
    })
}

const EMPTY: AvoidSet = { items: [], viewport: { width: 0, height: 0 } }
const NONE: readonly HelpId[] = []

/**
 * The avoid set of a step, kept current: a popup opens or closes, a declared window is moved
 * (style mutation) or resized, the page scrolls, the window resizes. Measured at most once per
 * frame; re-renders only when something actually changed.
 */
export function useAvoidSet(target: HTMLElement | null, avoid: readonly HelpId[] = NONE): AvoidSet {
  const [set, setSet] = useState<AvoidSet>(EMPTY)
  const key = avoid.join(' ')

  useLayoutEffect(() => {
    if (!target) { setSet(EMPTY); return }
    const ids = key ? (key.split(' ') as HelpId[]) : []
    let frame = 0
    let observed: Element[] = []
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => schedule()) : null
    const update = () => {
      frame = 0
      const next = collectAvoidSet(ids)
      setSet((prev) => (same(prev, next) ? prev : next))
      // Watch the size of what is on screen now (a window resized by its user).
      const els = next.items.map((i) => i.el)
      if (ro && (els.length !== observed.length || els.some((el, i) => el !== observed[i]))) {
        ro.disconnect()
        els.forEach((el) => ro.observe(el))
        observed = els
      }
    }
    const schedule = () => {
      if (frame) return
      if (typeof requestAnimationFrame === 'function') frame = requestAnimationFrame(update)
      else update()
    }
    update()
    const mo = new MutationObserver(schedule)
    mo.observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'open', 'aria-expanded', 'data-help-id'],
    })
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, true)
    return () => {
      if (frame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame)
      mo.disconnect()
      ro?.disconnect()
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, true)
    }
  }, [target, key])

  return set
}
